import { useState, useEffect } from "react";

/* ============================================================
   Talention — Navegación (hash router) y metadatos de ruta
   Único punto de verdad de la navegación de la app unificada.
   ============================================================ */

/* Navega a una ruta. Es una función de módulo (no depende del render de
   ningún componente), así que se puede llamar desde cualquier sitio y
   sobrevive a que el árbol de React se remonte (login/logout). */
function navigate(route) {
  window.location.hash = route;
}

/* Compatibilidad: las pantallas existentes navegan con window.GO.
   Se publica una sola vez, al cargar el módulo. */
window.GO = navigate;

/* Suscripción a la ruta actual. */
function useRoute() {
  const [route, setRoute] = useState(
    () => (window.location.hash || "#/dashboard").replace(/^#/, "")
  );
  useEffect(() => {
    const onHash = () =>
      setRoute((window.location.hash || "#/dashboard").replace(/^#/, ""));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return [route, navigate];
}

/* Metadatos por ruta: título, antetítulo y migas.
   Fusiona las dos tablas que había (PAGE_META en App.jsx, sin usar, y
   CRUMBS en igualdad-data.js). Única fuente para el topbar unificado. */
const ROUTE_META = {
  "/empresas": { eyebrow: "Consultoría", title: "Empresas", crumbs: ["Empresas"] },
  "/dashboard": { eyebrow: "Visión global", title: "Panel Dashboard", crumbs: ["Inicio"] },
  "/entity": { eyebrow: "Empresa", title: "Ficha de empresa", crumbs: ["Inicio", "Ficha de empresa"] },
  "/svpt/dashboard": { eyebrow: "SVPT", title: "Dashboard", crumbs: ["Inicio", "SVPT", "Dashboard"] },
  "/svpt/orgchart": { eyebrow: "SVPT", title: "Organigrama", crumbs: ["Inicio", "SVPT", "Organigrama"] },
  "/svpt/departments": { eyebrow: "SVPT", title: "Departamentos", crumbs: ["Inicio", "SVPT", "Departamentos"] },
  "/svpt/levels": { eyebrow: "SVPT", title: "Niveles jerárquicos", crumbs: ["Inicio", "SVPT", "Niveles jerárquicos"] },
  "/svpt/positions": { eyebrow: "SVPT", title: "Puestos", crumbs: ["Inicio", "SVPT", "Puestos"] },
  "/svpt/factors": { eyebrow: "SVPT", title: "Factores de valoración", crumbs: ["Inicio", "SVPT", "Factores de valoración"] },
  "/svpt/scales": { eyebrow: "SVPT", title: "Escalas salariales", crumbs: ["Inicio", "SVPT", "Escalas salariales"] },
  "/svpt/valuation": { eyebrow: "SVPT", title: "Valorar puestos", crumbs: ["Inicio", "SVPT", "Valorar puestos"] },
  "/igualdad": { eyebrow: "Retribución", title: "Inicio", crumbs: ["Inicio"] },
  "/igualdad/personas": { eyebrow: "Retribución", title: "Personas", crumbs: ["Inicio", "Personas"] },
  "/igualdad/puestos": { eyebrow: "Retribución", title: "Puestos", crumbs: ["Inicio", "Puestos"] },
  "/igualdad/registro": { eyebrow: "Retribución", title: "Registro retributivo", crumbs: ["Inicio", "Registro retributivo"] },
  "/igualdad/estructura": { eyebrow: "Retribución", title: "Políticas retributivas", crumbs: ["Inicio", "Políticas retributivas", "Estructura salarial"] },
  "/igualdad/analytics": { eyebrow: "Retribución", title: "People Analytics", crumbs: ["Inicio", "People Analytics"] },
  "/igualdad/incrementos": { eyebrow: "Retribución", title: "Incrementos salariales", crumbs: ["Inicio", "Incrementos salariales"] },
  "/igualdad/bandas": { eyebrow: "Retribución", title: "Bandas salariales por puesto", crumbs: ["Inicio", "Bandas salariales por puesto"] },
  "/igualdad/posicionamiento": { eyebrow: "Retribución", title: "Posicionamiento en banda", crumbs: ["Inicio", "Posicionamiento en banda"] },
  "/igualdad/evaluaciones": { eyebrow: "Retribución", title: "Evaluaciones", crumbs: ["Inicio", "Evaluaciones"] },
  "/igualdad/carga": { eyebrow: "Datos", title: "Carga masiva", crumbs: ["Inicio", "Carga masiva"] },
  "/igualdad/catalogos": { eyebrow: "Datos", title: "Catálogos", crumbs: ["Inicio", "Catálogos"] },
  "/igualdad/config": { eyebrow: "Administración", title: "Configuración", crumbs: ["Inicio", "Configuración"] },
  "/igualdad/tickets": { eyebrow: "Soporte", title: "Tickets", crumbs: ["Inicio", "Tickets"] },
};

/* Rutas cuyo nombre cambia con el método Ministerio del SVPT: la pantalla de
   escalas pasa a ser la de agrupaciones oficiales. */
const ROUTE_META_MINISTERIO = {
  "/svpt/scales": { eyebrow: "SVPT", title: "Agrupaciones", crumbs: ["Inicio", "SVPT", "Agrupaciones"] },
};

/* Rutas retiradas y su destino. Los deep-links antiguos no mueren en
   silencio: se redirigen al equivalente real. */
const REDIRECCIONES = {
  "/departments": "/igualdad/catalogos", // el catalogo real de departamentos
  "/levels": "/dashboard",               // concepto sin respaldo en el dominio
  "/users": "/igualdad/config",          // usuarios reales viven en Configuracion
  "/roles": "/igualdad/config",          // roles reales viven en Configuracion
  "/positions": "/igualdad/puestos",     // catalogo unico de puestos
  "/svpt": "/svpt/dashboard",            // la raiz del SVPT embebido es su dashboard
  // El motor de valoracion se retiro (spec 2026-09-09): sus rutas no mueren en
  // silencio, van al apartado SVPT, que es donde se valora ahora.
  "/valuation": "/svpt/valuation",
  "/scales": "/svpt/scales",
  "/metodologias": "/svpt/factors",
  "/factors": "/svpt/factors",
  "/proyectos-valoracion": "/svpt/dashboard",
};

/* Resuelve los metadatos de una ruta, tolerando rutas con parámetro
   (p. ej. /svpt/valuation/12 usa los de /svpt/valuation). Con el método
   Ministerio, las rutas de ROUTE_META_MINISTERIO usan su propio nombre. */
function metaDeRuta(route, metodoSvpt) {
  const clave = ROUTE_META[route]
    ? route
    : Object.keys(ROUTE_META)
      .filter((k) => k !== "/" && route.startsWith(k + "/"))
      .sort((a, b) => b.length - a.length)[0];
  if (!clave) return { eyebrow: "", title: "", crumbs: ["Inicio"] };
  if (metodoSvpt === "ministerio" && ROUTE_META_MINISTERIO[clave]) return ROUTE_META_MINISTERIO[clave];
  return ROUTE_META[clave];
}

export { useRoute, navigate, ROUTE_META, REDIRECCIONES, metaDeRuta };
